/**
 * Storefront API
 *
 * Public, customer-facing endpoints hit by the brand's public site. These
 * use the *customer* JWT (different scope than the dashboard's owner JWT),
 * and pass an explicit `brand_id` so they work both on the brand's custom
 * domain (where the Host header alone would do) AND on the platform's
 * `/site/:brandRef/...` preview routes.
 */
import { backendURL } from '../config';
import axios from 'axios';
import { loadSession } from './storefrontSession';

const url = (path, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, v); });
  const tail = qs.toString();
  return `${backendURL}/${path}${tail ? `?${tail}` : ''}`;
};

const headers = (brandId) => {
  const session = brandId ? loadSession(brandId) : null;
  const h = { 'Content-Type': 'application/json' };
  if (session?.token) h['Authorization'] = `Bearer ${session.token}`;
  return h;
};

const callJson = async ({ method, path, brandId, body, params }) => {
  try {
    const fullUrl = url(path, { ...(brandId ? { brand_id: brandId } : {}), ...(params || {}) });
    const res = await axios[method](fullUrl, body, { headers: headers(brandId), withCredentials: true });
    return res.data;
  } catch (err) {
    const data = err?.response?.data;
    throw new Error((data && (data.message || data.error)) || err?.message || 'Request failed');
  }
};

const getJson = async ({ path, brandId, params }) => {
  try {
    const fullUrl = url(path, { ...(brandId ? { brand_id: brandId } : {}), ...(params || {}) });
    const res = await axios.get(fullUrl, { headers: headers(brandId), withCredentials: true });
    return res.data;
  } catch (err) {
    const data = err?.response?.data;
    throw new Error((data && (data.message || data.error)) || err?.message || 'Request failed');
  }
};

export const StorefrontAPI = {
  // Public — anyone can hit these
  getStorefrontInfo: (brandId) => getJson({ path: 'public/storefront/info', brandId }),

  // Public catalog. The `filters` arg is forwarded as query params:
  // { q, category, min_price, max_price, sort }.
  listProducts: (brandId, filters = {}) =>
    getJson({ path: 'public/storefront/products/list', brandId, params: filters }),
  getProduct: (brandId, { slug, _id } = {}) =>
    getJson({ path: 'public/storefront/products/get', brandId, params: { slug, _id } }),

  signup: (brandId, body) =>
    callJson({ method: 'post', path: 'public/storefront/auth/signup', brandId, body }),
  login: (brandId, body) =>
    callJson({ method: 'post', path: 'public/storefront/auth/login', brandId, body }),
  logout: (brandId) =>
    callJson({ method: 'post', path: 'public/storefront/auth/logout', brandId, body: {} }),

  // Customer self-service — requires session
  me: (brandId) => getJson({ path: 'public/storefront/me', brandId }),
  updateMe: (brandId, patch) =>
    callJson({ method: 'post', path: 'public/storefront/me/update', brandId, body: { patch } }),
  addAddress: (brandId, address) =>
    callJson({ method: 'post', path: 'public/storefront/me/addresses/add', brandId, body: { address } }),
  deleteAddress: (brandId, address_id) =>
    callJson({ method: 'post', path: 'public/storefront/me/addresses/delete', brandId, body: { address_id } }),

  // Orders
  listMyOrders: (brandId, limit) =>
    callJson({ method: 'post', path: 'public/storefront/orders/list', brandId, body: { limit } }),
  getMyOrder: (brandId, _id) =>
    callJson({ method: 'post', path: 'public/storefront/orders/get', brandId, body: { _id } }),
  placeOrder: (brandId, body) =>
    callJson({ method: 'post', path: 'public/storefront/orders/place', brandId, body }),
  // Place the order AND open a Stripe Checkout Session (when the brand has
  // Stripe configured). Returns `{ order, payment }`. When `payment` is
  // `null` the brand hasn't wired Stripe — caller falls back to its
  // in-app confirmation page.
  checkout: (brandId, body) =>
    callJson({ method: 'post', path: 'public/storefront/orders/checkout', brandId, body }),
  cancelMyOrder: (brandId, _id, reason) =>
    callJson({ method: 'post', path: 'public/storefront/orders/cancel', brandId, body: { _id, reason } }),

  // Preview a discount code against the current cart subtotal. Returns
  // `{ ok, amount, code, ... }` when valid; `{ ok:false, reason }` otherwise.
  previewDiscount: (brandId, { code, subtotal, currency }) =>
    callJson({
      method: 'post',
      path: 'public/storefront/discounts/preview',
      brandId,
      body: { code, subtotal, currency },
    }),

  // ── Product reviews ───────────────────────────────────────────────────────
  listProductReviews: (brandId, productId, limit = 50) =>
    getJson({
      path: 'public/storefront/products/reviews/list',
      brandId,
      params: { product_id: productId, limit },
    }),
  createProductReview: (brandId, { product_id, rating, title, body, author_name, order_id }) =>
    callJson({
      method: 'post',
      path: 'public/storefront/products/reviews/create',
      brandId,
      body: { product_id, rating, title, body, author_name, order_id },
    }),

  /**
   * ── Cart ─────────────────────────────────────────────────────────────────
   *
   * The browser keeps its own in localStorage — someone shopping without an
   * account has nowhere else to put a basket. These are the other half: once
   * somebody has told us who they are, their basket follows them between
   * devices. `cartMerge` folds the browser's into the stored one at sign-in.
   */
  cartGet: (brandId) =>
    callJson({ method: 'post', path: 'public/storefront/cart/get', brandId, body: {} }),
  cartSet: (brandId, items) =>
    callJson({ method: 'post', path: 'public/storefront/cart/set', brandId, body: { items } }),
  cartMerge: (brandId, items) =>
    callJson({ method: 'post', path: 'public/storefront/cart/merge', brandId, body: { items } }),
  cartClear: (brandId) =>
    callJson({ method: 'post', path: 'public/storefront/cart/clear', brandId, body: {} }),

  /**
   * ── Support tickets ──────────────────────────────────────────────────────
   *
   * A conversation about one thing, which the customer can come back and
   * read. The contact form is a message into a void by comparison: the reply
   * arrives by email and the thread lives in somebody's inbox.
   */
  ticketOpen: (brandId, body) =>
    callJson({ method: 'post', path: 'public/storefront/tickets/open', brandId, body }),
  ticketList: (brandId) =>
    callJson({ method: 'post', path: 'public/storefront/tickets/list', brandId, body: {} }),
  ticketGet: (brandId, _id) =>
    callJson({ method: 'post', path: 'public/storefront/tickets/get', brandId, body: { _id } }),
  ticketReply: (brandId, _id, body) =>
    callJson({ method: 'post', path: 'public/storefront/tickets/reply', brandId, body: { _id, body } }),

  // ── Wishlist ──────────────────────────────────────────────────────────────
  listWishlist: (brandId) =>
    callJson({ method: 'post', path: 'public/storefront/wishlist/list', brandId, body: {} }),
  toggleWishlist: (brandId, product_id) =>
    callJson({ method: 'post', path: 'public/storefront/wishlist/toggle', brandId, body: { product_id } }),
  wishlistContains: (brandId, product_id) =>
    getJson({ path: 'public/storefront/wishlist/contains', brandId, params: { product_id } }),

  // Anonymous order lookup — guest checkouts and shoppers without an
  // account use this to see status by number + email.
  trackOrder: (brandId, { number, email }) =>
    callJson({
      method: 'post',
      path: 'public/storefront/orders/track',
      brandId,
      body: { number, email },
    }),

  // Public contact form — backend resolves the brand from the host or
  // the explicit brand_id in the URL (handled by the shared helper).
  submitContact: (brandId, { name, email, subject, body }) =>
    callJson({
      method: 'post',
      path: 'public/storefront/contact',
      brandId,
      body: { name, email, subject, body },
    }),
};

export default StorefrontAPI;
